import React, { useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthStore } from '../store/authStore';
import { useUiStore } from '../store/uiStore'; // Import useUiStore
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface AuthInitializerProps {
  children: React.ReactNode;
}

const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const { isLoading: isAuth0Loading, isAuthenticated, error: auth0Error } = useAuth0();
  const { isApiAuthReady, isSetupComplete } = useAuthStore(state => ({
    isApiAuthReady: state.isApiAuthReady,
    isSetupComplete: state.isSetupComplete,
  }));
  const { setGlobalLoading, globalLoadingStatus, cacheRestorationComplete } = useUiStore();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Subscribe to cache changes by using useQuery (similar to ReactSafeDashboard approach)
  const { userTimezone } = useAuthStore.getState();
  const effectiveTimezone = userTimezone || 'UTC';
  const { data: pnlTimeframesData } = useQuery({
    queryKey: ['pnlTimeframes', effectiveTimezone],
    queryFn: () => null, // This will never be called
    enabled: false,      // We only want to read from cache
    staleTime: Infinity,
  });

  // Check if we have cached dashboard data to avoid showing spinner unnecessarily
  const hasCachedDashboardData = useMemo(() => {
    if (!queryClient) return false;

    try {
      const { userTimezone } = useAuthStore.getState();
      const effectiveTimezone = userTimezone || 'UTC';
      
      // Check the same queries that ReactSafeDashboard and Dashboard check
      const pnlTimeframes = queryClient.getQueryData(['pnlTimeframes', effectiveTimezone]);
      const accountSummary = queryClient.getQueryData(['accountSummary']);
      const strategySummary = queryClient.getQueryData(['strategySummary']);
      const accountDaily = queryClient.getQueryData(['accountDaily', effectiveTimezone]);
      const monthlyCalendar = queryClient.getQueryData(['monthlyCalendar', effectiveTimezone]);
      const accountBalances = queryClient.getQueryData(['accountBalances']);
      
      const hasCache = Boolean(
        pnlTimeframes ||
        pnlTimeframesData || // Use the reactive data from useQuery as fallback
        (accountSummary && Array.isArray(accountSummary) && accountSummary.length > 0) ||
        (strategySummary && Array.isArray(strategySummary) && strategySummary.length > 0) ||
        (accountDaily && Array.isArray(accountDaily) && accountDaily.length > 0) ||
        (monthlyCalendar && Array.isArray(monthlyCalendar) && monthlyCalendar.length > 0) ||
        (accountBalances && 
         typeof accountBalances === 'object' && 
         'data' in accountBalances && 
         Array.isArray((accountBalances as any).data) && 
         (accountBalances as any).data.length > 0)
      );
      
      return hasCache;
    } catch (error) {
      console.error('[AuthInitializer] Error checking cached data:', error);
      return false;
    }
  }, [queryClient, pnlTimeframesData]); // Re-run when queryClient or pnlTimeframesData changes

  // Simplified message for Auth0 initialization only
  const authMessage = "Initializing Authentication...";// Effect to log state changes for debugging
  useEffect(() => {
    console.log('[AuthInitializer] State update:', {
      isAuth0Loading,
      isAuthenticated,
      isApiAuthReady,
      isSetupComplete,
      auth0Error: auth0Error?.message,
      globalSpinnerActive: globalLoadingStatus.isActive,
      globalSpinnerMessage: globalLoadingStatus.message,    });
  }, [isAuth0Loading, isAuthenticated, isApiAuthReady, isSetupComplete, auth0Error, globalLoadingStatus]);  useEffect(() => {
    // Check if we're on dashboard route and have cached data
    const isDashboardRoute = location.pathname === '/dashboard';
    const hasCache = isDashboardRoute ? hasCachedDashboardData : false;
    
    // Check if user is authenticated but not yet on dashboard (during navigation transition)
    const isNavigatingToDashboard = isAuthenticated && !isDashboardRoute && location.pathname === '/login';
    
    // CRITICAL: Only show spinner for dashboard-related loading, not general Auth0 loading on other routes
    // Also, don't show spinner for Auth0 loading when user is already authenticated (post-login token refresh)
    const shouldShowSpinnerForAuth0 = isAuth0Loading && !isAuthenticated && (isDashboardRoute || isNavigatingToDashboard);
    
    console.log('[AuthInitializer] 🔄 Loading state logic:', {
      isAuth0Loading,
      isAuthenticated,
      isDashboardRoute,
      hasCachedDashboardData,
      hasCache,
      cacheRestorationComplete,
      isNavigatingToDashboard,
      shouldShowSpinnerForAuth0,
      shouldShowSpinner: shouldShowSpinnerForAuth0 || isNavigatingToDashboard,
      currentPath: location.pathname,
      currentGlobalStatus: useUiStore.getState().globalLoadingStatus
    });
    
    // If we have cached data on dashboard route AND cache restoration is complete, never show spinner
    if (isDashboardRoute && hasCache && cacheRestorationComplete) {
      const currentGlobalStatus = useUiStore.getState().globalLoadingStatus;
      if (currentGlobalStatus.isActive && currentGlobalStatus.source === 'AuthInitializer') {
        console.log('[AuthInitializer] ✅ Clearing loading state immediately - dashboard has cached data and cache restoration complete');
        setGlobalLoading(false, null, 'AuthInitializer');
      }
      return; // Early return to prevent setting spinner
    }
    
    // Show spinner for navigation to dashboard or initial Auth0 loading (but not post-auth token refresh)
    if (shouldShowSpinnerForAuth0 || isNavigatingToDashboard) {
      let message = authMessage;
      if (isNavigatingToDashboard && !isAuth0Loading) {
        message = 'Preparing dashboard...';
      } else if (isAuthenticated && isAuth0Loading) {
        // Don't show spinner for post-authentication token refresh
        console.log('[AuthInitializer] 🔄 Skipping spinner - post-authentication token refresh');
        return;
      }
      console.log('[AuthInitializer] 🔄 Setting loading state - Auth0 loading for dashboard or navigating to dashboard');
      setGlobalLoading(true, message, 'AuthInitializer');
    } else {
      // Clear loading state when Auth0 loading completes OR when we're not in a dashboard-related flow
      const currentGlobalStatus = useUiStore.getState().globalLoadingStatus;
      
      const shouldClearLoading = currentGlobalStatus.isActive && 
        currentGlobalStatus.source === 'AuthInitializer' && 
        (!shouldShowSpinnerForAuth0 && !isNavigatingToDashboard);
      
      if (shouldClearLoading) {
        console.log('[AuthInitializer] ✅ Clearing loading state:', {
          reason: 'Auth0 loading not dashboard-related or complete',
          isDashboardRoute,
          hasCache: isDashboardRoute && hasCache,
          isNavigatingToDashboard,
          shouldShowSpinnerForAuth0,
          isAuthenticated,
          currentStatus: currentGlobalStatus
        });
        setGlobalLoading(false);
      } else if (isDashboardRoute && !hasCache && currentGlobalStatus.isActive && currentGlobalStatus.source === 'AuthInitializer') {
        // If we're going to dashboard without cache, keep the loading active but change message
        console.log('[AuthInitializer] 🔄 Keeping loading active for dashboard data loading');
        setGlobalLoading(true, 'Loading Dashboard...', 'AuthInitializer');
      }
    }
  }, [isAuth0Loading, isAuthenticated, setGlobalLoading, authMessage, location.pathname, hasCachedDashboardData, cacheRestorationComplete]);

  // Determine if children should be rendered
  // const allowChildren = !isAuth0Loading && !auth0Error &&
  //                       (!isAuthenticated || (isApiAuthReady && isSetupComplete !== null));

  // if (!allowChildren) {
  //   // Prevent rendering its children if the global spinner is active with a message that AuthInitializer recognizes
  //   // This condition should ideally not be met for long if the useEffect logic above correctly clears the spinner.
  //   // It acts as a safeguard during the brief period AuthInitializer might set the spinner before its own effect clears it upon condition change.
  //   if (globalLoadingStatus.isActive && 
  //       (globalLoadingStatus.message === AUTH_INITIALIZER_LOADING_MESSAGE ||
  //        Object.values(messages).includes(globalLoadingStatus.message || ""))
  //      ) {
  //     return null;
  //   }
  // }

  return <>{children}</>; // Always render children to allow Suspense/RouteLoadingFallback to engage
};

export default AuthInitializer;
